import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router'
import ErrorPage from '../../modules/app/pages/ErrorPage.vue'
import SettingsPage from '../../modules/settings/pages/SettingsPage.vue'
import OrganizationGeneralSettingsPage from '../../modules/settings/pages/OrganizationGeneralSettingsPage.vue'
import OrganizationMembersSettingsPage from '../../modules/settings/pages/OrganizationMembersSettingsPage.vue'
import ManageFeedbackTopicsPage from '../../modules/settings/pages/ManageFeedbackTopicsPage.vue'
import OrganizationTeamsSettingsPage from '../../modules/settings/pages/OrganizationTeamsSettingsPage.vue'
import OrganizationTeamSettingsPage from '../../modules/settings/pages/OrganizationTeamSettingsPage.vue'
import PersonalSettingsPage from '../../modules/settings/pages/PersonalSettingsPage.vue'
import NotificationsSettingsPage from '../../modules/settings/pages/NotificationsSettingsPage.vue'
import OrganizationSwitchPage from '../../modules/organization/pages/OrganizationSwitchPage.vue'
import JoinOrganizationPage from '../../modules/organization/pages/JoinOrganizationPage.vue'
import { useApplicationModule } from '@/core/composables/use-application-module'
import { usePermission } from '@/core/composables/use-permission'
import { ApplicationModule } from '@/core/models/organization'
import { Permission } from '@/core/models/user'

const redirectToDefaultOrganizationSettings = (): RouteLocationRaw => {
  const { can } = usePermission()
  if (can(Permission.ORG_MANAGE_SETTINGS)) {
    return { name: 'settings-organization-general' }
  }
  if (can(Permission.ORG_MANAGE_MEMBERS)) {
    return { name: 'settings-organization-members' }
  }
  return { name: 'settings-personal' }
}

const redirectToDefaultSettings = (): RouteLocationRaw => {
  const { can } = usePermission()
  if (can(Permission.ORG_MANAGE_SETTINGS) || can(Permission.ORG_MANAGE_MEMBERS)) {
    return redirectToDefaultOrganizationSettings()
  }
  return { name: 'settings-personal' }
}

export const sharedRoutes: RouteRecordRaw[] = [
  {
    path: '/error',
    name: 'error-page',
    component: ErrorPage,
    meta: { layout: 'full-screen', isPublic: true },
    props: (route) => ({
      type: route.query.type ?? 'systemError',
    }),
  },
  {
    path: '/choose-organization',
    name: 'choose-organization',
    component: OrganizationSwitchPage,
    meta: { layout: 'full-screen' },
  },
  {
    path: '/join-organization',
    name: 'join-organization',
    component: JoinOrganizationPage,
    meta: { layout: 'full-screen' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsPage,
    meta: { layout: 'settings' },
    redirect: redirectToDefaultSettings,
    children: [
      {
        path: 'organization',
        redirect: redirectToDefaultOrganizationSettings,
      },
      {
        path: 'organization/general',
        name: 'settings-organization-general',
        component: OrganizationGeneralSettingsPage,
      },
      {
        path: 'organization/members',
        name: 'settings-organization-members',
        component: OrganizationMembersSettingsPage,
      },
      {
        path: 'organization/feedback-topics',
        name: 'settings-organization-feedback-topics',
        component: ManageFeedbackTopicsPage,
      },
      {
        path: 'organization/teams',
        name: 'settings-organization-teams',
        component: OrganizationTeamsSettingsPage,
      },
      {
        path: 'organization/teams/:teamId',
        name: 'settings-organization-team',
        component: OrganizationTeamSettingsPage,
        props: true,
      },
      {
        path: 'personal',
        redirect: { name: 'settings-personal' },
      },
      {
        path: 'personal/general',
        name: 'settings-personal',
        component: PersonalSettingsPage,
      },
      {
        path: 'personal/notifications',
        name: 'settings-personal-notifications',
        component: NotificationsSettingsPage,
        beforeEnter: () => {
          const { hasModule } = useApplicationModule()
          if (hasModule(ApplicationModule.LUMIA)) {
            return { name: 'settings-personal' }
          }
        },
      },
    ],
  },
]
